// Ce que les composants utilisent :
//   const t = useT('cart', 'common');                  // déclare les espaces de noms du composant
//   t('cart.title')   t('cart.total', { amount })   t('cart.items', { count })
//   <T k="home.intro" components={{ lien: <Link to="/x" /> }} />      // phrase avec liens / gras
//   const { lang, languages, setLanguage } = useLang();
//   const fmt = useFormat();   fmt.money(12.5)   fmt.date('2026-09-19', 'long')   fmt.number(1234.5)
import React, { useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { I18nContext } from './context';
import { DEFAULT_LANG, isEnabledLang } from './config';
import { getDict, getVersion, isLoaded, loadNamespaces, makeT, subscribe } from './store';
import { makeFormat } from './format';

export const useLang = () => useContext(I18nContext);

/**
 * Déclare les espaces de noms utilisés par le composant et renvoie t().
 * Si l'un d'eux n'est pas encore chargé (langue autre que le français), le composant « suspend » :
 * React affiche le fallback du <Suspense> le plus proche jusqu'à l'arrivée du fichier — jamais de texte
 * français qui clignote. Le français, lui, ne suspend jamais.
 */
export const useT = (...namespaces) => {
  const { lang } = useContext(I18nContext);
  const version = useSyncExternalStore(subscribe, getVersion);
  if (lang !== DEFAULT_LANG) {
    const missing = namespaces.filter((ns) => !isLoaded(lang, ns));
    if (missing.length) throw loadNamespaces(lang, missing); // interceptée par <Suspense>
  }
  // `version` change quand un espace vient d'être chargé : t() est alors recréée.
  return useMemo(() => makeT(lang), [lang, version]); // eslint-disable-line react-hooks/exhaustive-deps
};

/** Phrase avec liens ou gras : <T k="home.intro" values={{ n: 3 }} components={{ lien: <Link to="/x" /> }} /> */
export const T = ({ k, values, components }) => {
  const t = useT(String(k).split('.')[0]);
  return <>{t.rich(k, values, components)}</>;
};

export const useFormat = () => {
  const { locale } = useContext(I18nContext);
  return useMemo(() => makeFormat(locale), [locale]);
};

/**
 * Pages de destinataires (faire-part, carte) : impose la langue choisie par l'acheteur pour la page
 * (ex. params.langue du faire-part). Sans effet si la langue n'est pas active sur ce site.
 */
export const useRecipientLang = (code) => {
  const { setOverride } = useContext(I18nContext);
  useEffect(() => {
    setOverride(code && isEnabledLang(code) ? code : null);
    return () => setOverride(null);
  }, [code, setOverride]);
};

// Réservé aux outils/tests : lecture directe d'un dictionnaire chargé.
export const peekDictionary = getDict;
